'use client'

import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons'
import { motion, useWillChange } from 'motion/react'
import Image from 'next/image'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { PaginationDots } from '@/components/pagination-dots'
import { throttle } from '@/utils/functions/throttle'
import { cn } from '@/utils/styles/cn'

interface ICommonSliderTestimonialsProps {
    type: 'testimonials'
    testimonials: { image: string; quote: string; name: string; position: string }[]
    className?: string
}

interface ICommonSliderContentProps {
    type: 'content'
    content: { image: string; title: string; position?: string; text: string }[]
    className?: string
}

type ICommonSliderProps = ICommonSliderTestimonialsProps | ICommonSliderContentProps

export function CommonSlider(props: ICommonSliderProps) {
    const { type, className } = props

    const mobileScrollContainerRef = useRef<HTMLDivElement>(null)
    const desktopScrollContainerRef = useRef<HTMLDivElement>(null)

    const [currentImagePage, setCurrentImagePage] = useState(1)
    const [maxHeight, setMaxHeight] = useState(0)

    const desktopRefs = useRef<Record<number, HTMLDivElement>>({})
    const mobileRefs = useRef<Record<number, HTMLDivElement>>({})

    const [isAtStart, setIsAtStart] = useState(true)
    const [isAtEnd, setIsAtEnd] = useState(false)

    const makeRef = useCallback(
        (type: 'mobile' | 'desktop') => (index: number) => (element: HTMLDivElement) => {
            if (!element) return

            switch (type) {
                case 'mobile':
                    mobileRefs.current[index] = element
                    break
                case 'desktop':
                    desktopRefs.current[index] = element
                    break
            }
        },
        []
    )

    useLayoutEffect(() => {
        const calculateMaxHeight = () => {
            const desktopHeights = Object.values(desktopRefs.current).map((ref) => ref.clientHeight)
            const mobileHeights = Object.values(mobileRefs.current).map((ref) => ref.clientHeight)

            const allHeights = [...desktopHeights, ...mobileHeights]
            setMaxHeight(Math.max(0, ...allHeights))
        }

        calculateMaxHeight()

        const resizeObserver = new ResizeObserver(calculateMaxHeight)
        Object.values(desktopRefs.current).forEach((ref) => ref && resizeObserver.observe(ref))
        Object.values(mobileRefs.current).forEach((ref) => ref && resizeObserver.observe(ref))

        return () => {
            resizeObserver.disconnect()
        }
    }, [])

    const onScroll = useCallback(
        (type: 'mobile' | 'desktop') =>
            throttle(() => {
                const container =
                    type === 'mobile'
                        ? mobileScrollContainerRef.current
                        : desktopScrollContainerRef.current

                if (!container) return

                const pageWidth = container.clientWidth
                const currentPage = Math.round(container.scrollLeft / pageWidth) + 1

                if (currentPage !== currentImagePage) {
                    setCurrentImagePage(currentPage)
                }
            }, 200),
        [currentImagePage]
    )

    const scrollToPage = useCallback((page: number, type: 'mobile' | 'desktop') => {
        const container =
            type === 'mobile' ? mobileScrollContainerRef.current : desktopScrollContainerRef.current

        if (!container) return

        const pageWidth = container.clientWidth
        container.scrollTo({
            left: (page - 1) * pageWidth,
            behavior: 'smooth',
        })
        setCurrentImagePage(page)
    }, [])

    const willChange = useWillChange()

    const updateButtonStates = useCallback(() => {
        if (desktopScrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = desktopScrollContainerRef.current
            setIsAtStart(scrollLeft === 0)
            setIsAtEnd(scrollLeft + clientWidth >= scrollWidth - 323)
        }
    }, [])

    const scrollLeft = () => {
        if (desktopScrollContainerRef.current) {
            desktopScrollContainerRef.current.scrollBy({ left: -200, behavior: 'smooth' })
        }
    }

    const scrollRight = () => {
        if (desktopScrollContainerRef.current) {
            desktopScrollContainerRef.current.scrollBy({ left: 200, behavior: 'smooth' })
        }
    }

    useEffect(() => {
        const scrollableDiv = desktopScrollContainerRef.current
        if (scrollableDiv) {
            updateButtonStates()
            scrollableDiv.addEventListener('scroll', updateButtonStates)
            return () => scrollableDiv.removeEventListener('scroll', updateButtonStates)
        }
    }, [updateButtonStates])

    return (
        <div className="w-full max-w-[1300px] flex flex-col items-center relative">
            <button
                onClick={scrollLeft}
                style={{ zIndex: 1 }}
                className={`hidden md:block md:absolute bottom-52 left-4 p-4 rounded-full shadow-md bg-white hover:shadow-lg ${
                    isAtStart ? 'text-gray-200' : 'text-gray-400'
                }`}
                disabled={isAtStart}
            >
                <ChevronLeftIcon className="h-8 w-8" />
            </button>
            <button
                onClick={scrollRight}
                disabled={isAtEnd}
                style={{ zIndex: 1 }}
                className={`hidden md:block md:absolute bottom-52 right-4 p-4 rounded-full shadow-md bg-white hover:shadow-lg ${
                    isAtEnd ? 'text-gray-200' : 'text-gray-400'
                }`}
            >
                <ChevronRightIcon className="h-8 w-8" />
            </button>
            <motion.div
                initial={{ height: '100%' }}
                animate={{ height: maxHeight }}
                transition={{
                    type: 'spring',
                    stiffness: 600,
                    damping: 50,
                }}
                onScroll={onScroll('desktop')}
                ref={desktopScrollContainerRef}
                className={cn(
                    `hidden md:flex absolute md:relative flex-row items-start gap-x-1 overflow-x-auto overflow-y-hidden
                    snap-x snap-mandatory scrollbar-hide max-w-[70vw]`,
                    className
                )}
            >
                {type === 'testimonials' &&
                    props.testimonials.map((testimonial, index) => (
                        <TestimonialPage
                            index={index}
                            testimonial={testimonial}
                            makeRef={makeRef('desktop')}
                            key={`desktop-${index}`}
                        />
                    ))}

                {type === 'content' &&
                    props.content.map((item, index) => (
                        <ContentPage
                            index={index}
                            content={item}
                            makeRef={makeRef('desktop')}
                            key={`desktop-${index}`}
                        />
                    ))}
                <div className="flex-shrink-0 w-[20%]" />
            </motion.div>

            <motion.div
                initial={{ height: '100%' }}
                animate={{ height: maxHeight }}
                transition={{
                    type: 'spring',
                    stiffness: 900,
                    damping: 70,
                }}
                style={{ willChange }}
                className="w-dvw md:hidden"
            >
                <motion.div
                    onScroll={onScroll('mobile')}
                    ref={mobileScrollContainerRef}
                    className={cn(
                        `flex md:hidden md:absolute flex-row items-start gap-x-0 overflow-y-hidden overflow-x-auto snap-x
                                    snap-mandatory scrollbar-hide max-w-[100vw]`
                    )}
                >
                    {type === 'testimonials' &&
                        props.testimonials.map((testimonial, index) => (
                            <TestimonialPage
                                index={index}
                                testimonial={testimonial}
                                makeRef={makeRef('mobile')}
                                key={`mobile-${index}`}
                            />
                        ))}

                    {type === 'content' &&
                        props.content.map((item, index) => (
                            <ContentPage
                                index={index}
                                content={item}
                                makeRef={makeRef('mobile')}
                                key={`mobile-${index}`}
                            />
                        ))}
                </motion.div>
            </motion.div>

            <PaginationDots
                currentPage={currentImagePage}
                pagesCount={
                    type === 'testimonials' ? props.testimonials.length : props.content.length
                }
                className="mt-3 md:mt-10"
                activeColorClassName="bg-black scale-2"
                inactiveColorClassName="bg-black/50"
                onPageChange={(page) => {
                    scrollToPage(page, 'mobile')
                    scrollToPage(page, 'desktop')
                }}
            />
        </div>
    )
}

interface ITestimonialPageProps {
    testimonial: {
        image: string
        quote: string
        name: string
        position: string
    }
    index: number
    makeRef: (index: number) => (element: HTMLDivElement) => void
}

const TestimonialPage = ({ testimonial, index, makeRef }: ITestimonialPageProps) => {
    return (
        <div
            key={'common-slider-testimonials' + testimonial.name + testimonial.position + index}
            className="relative flex md:flex-row flex-col flex-shrink-0 snap-center snap-always md:snap-none min-w-[100vw] max-w-[100vw] md:min-w-[70vw] md:max-w-[70vw] justify-center items-center md:items-start px-4 md:px-0"
        >
            <div
                ref={makeRef(index)}
                className="flex md:flex-row flex-col justify-start md:justify-center items-center w-full bg-black/5 md:bg-white gap-6 rounded-[16px] py-6 px-4 md:py-0 md:px-0"
            >
                <Image
                    priority={index === 0}
                    src={testimonial.image}
                    alt={testimonial.name}
                    height={224}
                    width={224}
                    className="rounded-[16px] overflow-hidden h-[190px] w-[190px] md:h-[324px] md:w-[324px] object-cover"
                />

                <div className="flex flex-col items-start max-w-full md:max-w-[490px] mt-1 w-full">
                    <h4 className="text-left font-medium md:font-bold text-[14px] md:text-[20px] mb-4 leading-[1.5] md:text-left">
                        {testimonial.quote}
                    </h4>

                    <p className="text-left md:text-left text-[12px] md:text-[14px] text-black/50 font-medium">
                        {testimonial.name}, {testimonial.position}
                    </p>
                </div>
            </div>
        </div>
    )
}

interface IContentPageProps {
    content: { image: string; title: string; position?: string; text: string }
    index: number
    makeRef: (index: number) => (element: HTMLDivElement) => void
}

const ContentPage = ({ content, index, makeRef }: IContentPageProps) => {
    return (
        <div
            key={'common-slider-content' + content.title + content.text + index}
            className="relative flex md:flex-row flex-col flex-shrink-0 snap-center snap-always md:snap-none min-w-[100vw] max-w-[100vw] md:min-w-[70vw] md:max-w-[70vw] justify-center items-center md:items-start px-4 md:px-0"
        >
            <div
                ref={makeRef(index)}
                className="flex md:flex-row flex-col justify-start md:justify-center items-center w-full bg-black/5 md:bg-white gap-6 rounded-[16px] py-6 px-4 md:py-0 md:px-0"
            >
                <Image
                    priority={index === 0}
                    src={content.image}
                    alt={content.title}
                    height={324}
                    width={324}
                    className="rounded-[16px] overflow-hidden h-[190px] w-[190px] md:h-[324px] md:w-[324px] object-contain"
                />
                <div className="flex flex-col items-start max-w-full md:max-w-[490px] mt-1 w-full">
                    <h4
                        className={cn(
                            'text-[20px] md:text-[24px] leading-[1.5] text-left',
                            !content.position && 'mb-4'
                        )}
                    >
                        {content.title}
                    </h4>
                    {content.position && (
                        <p className="text-left text-[14px] md:text-[16px] mb-4 text-black/80 font-bold">
                            {content.position}
                        </p>
                    )}

                    <p className="text-left text-[16px] text-black/80 font-medium">
                        {content.text}
                    </p>
                </div>
            </div>
        </div>
    )
}
